import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Grid, Snackbar, Switch, Typography, useTheme } from '@mui/material';
import AiAgentService from '../../services/aiAgent.service';
import AiAgentSettings from './AiAgentSettings';
import AiCallComposer from './AiCallComposer';
import AiCallHistory from './AiCallHistory';
import { cueLabelSx, getTokens } from './aiAgentTheme';

/**
 * Obrazovka hlasovej linky.
 *
 * Poradie je podľa toho, ako často sa vec používa: hore stav linky, hneď pod
 * ním spustenie hovoru a jeho výsledok, a až celkom dole zbalený scenár
 * asistenta. Predtým bolo nastavovanie prvé a volanie až kdesi pod ním.
 */
const AiAgentPage = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const tokens = getTokens(isDark, theme.palette.primary.main);

    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [historyKey, setHistoryKey] = useState(0);
    const [toast, setToast] = useState(null);

    useEffect(() => {
        let alive = true;
        Promise.all([
            AiAgentService.getConfig().catch((err) => {
                setToast({ type: 'error', text: err?.response?.data?.message || 'Nastavenia sa nenačítali' });
                return { data: null };
            }),
            AiAgentService.getEmployees().catch((err) => {
                setToast({ type: 'error', text: err?.response?.data?.message || 'Zoznam ľudí sa nenačítal' });
                return { data: [] };
            }),
        ]).then(([configRes, employeesRes]) => {
            if (!alive) return;
            setConfig(configRes.data);
            setEmployees(Array.isArray(employeesRes.data) ? employeesRes.data : []);
            setLoading(false);
        });
        return () => {
            alive = false;
        };
    }, []);

    const setField = (key) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setConfig((prev) => ({ ...prev, [key]: value }));
    };

    const handleSave = () => {
        setSaving(true);
        AiAgentService.saveConfig({
            ...config,
            joinDelayMs: Number(config.joinDelayMs),
            minGapSeconds: Number(config.minGapSeconds),
            idleNarrateSeconds: Number(config.idleNarrateSeconds),
            changeRatio: Number(config.changeRatio),
            remindShareSeconds: Number(config.remindShareSeconds || 0),
        })
            .then((res) => {
                setConfig(res.data);
                setToast({ type: 'success', text: 'Scenár uložený — platí od ďalšieho hovoru' });
            })
            .catch((err) => setToast({ type: 'error', text: err?.response?.data?.message || 'Scenár sa neuložil' }))
            .finally(() => setSaving(false));
    };

    const handleCalled = (mode, data, employee) => {
        const who = mode === 'app' ? employee?.name : data?.employeeName || data?.phone;
        setToast({ type: 'success', text: mode === 'app' ? `Zvoní ${who}` : `Vytáčam ${who}` });
        setHistoryKey((k) => k + 1);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress size={26} />
            </Box>
        );
    }

    if (!config) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Nastavenia linky sa nenačítali. Skús obnoviť stránku.</Alert>
            </Box>
        );
    }

    const live = !!config.enabled;
    const tagSx = {
        fontSize: 11.5,
        color: tokens.inkMuted,
        border: `1px solid ${tokens.hairline}`,
        borderRadius: '2px',
        px: 0.75,
        py: 0.25,
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1240, mx: 'auto' }}>
            {/* Pult linky – jediné, čo tu treba vedieť na prvý pohľad, je či je zapnutá */}
            <Box
                sx={{
                    border: `1px solid ${tokens.hairline}`,
                    borderLeft: `2px solid ${live ? tokens.accent : tokens.spine}`,
                    bgcolor: tokens.surfaceSunken,
                    px: { xs: 2, md: 3 },
                    py: 2.5,
                    mb: 4,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={cueLabelSx(tokens)}>Hlasová linka</Typography>
                        <Typography
                            sx={{
                                fontSize: { xs: 24, md: 28 },
                                fontWeight: 700,
                                letterSpacing: '-0.01em',
                                lineHeight: 1.2,
                                color: tokens.ink,
                                mt: 0.5,
                            }}
                        >
                            {live ? 'Linka je zapnutá' : 'Linka je vypnutá'}
                        </Typography>
                        <Typography sx={{ color: tokens.inkMuted, fontSize: 14, mt: 0.75, maxWidth: 560 }}>
                            Asistent zavolá zamestnancovi do aplikácie alebo na telefón, vybaví, čo mu zadáš, a
                            hovor sa zapíše do záznamu.
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', gap: 0.75 }}>
                            <Box component="span" sx={tagSx}>
                                {config.model}
                            </Box>
                            <Box component="span" sx={tagSx}>
                                hlas: {config.voice}
                            </Box>
                        </Box>
                        <Switch checked={live} onChange={setField('enabled')} />
                    </Box>
                </Box>
            </Box>

            {/* Spustenie hovoru a jeho výsledok vedľa seba – jedno vedie k druhému */}
            <Grid container spacing={{ xs: 3, md: 4 }}>
                <Grid item xs={12} md={5}>
                    <Typography sx={{ fontSize: 17, fontWeight: 600, color: tokens.ink, mb: 1.5 }}>
                        Nový hovor
                    </Typography>
                    <AiCallComposer
                        employees={employees}
                        disabled={!live}
                        onCalled={handleCalled}
                        onError={(text) => setToast({ type: 'error', text })}
                    />
                </Grid>

                <Grid item xs={12} md={7}>
                    <AiCallHistory reloadKey={historyKey} />
                </Grid>
            </Grid>

            <AiAgentSettings config={config} setField={setField} saving={saving} onSave={handleSave} />

            <Snackbar
                open={!!toast}
                autoHideDuration={5000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {toast ? (
                    <Alert severity={toast.type} onClose={() => setToast(null)} sx={{ width: '100%' }}>
                        {toast.text}
                    </Alert>
                ) : null}
            </Snackbar>
        </Box>
    );
};

export default AiAgentPage;
